import {type ClassValue, clsx} from 'clsx'
import {extendTailwindMerge} from 'tailwind-merge'

const customTwMerge = extendTailwindMerge({
    extend: {
        classGroups: {
            'font-size': [
                'text-display',
                'text-h1',
                'text-h2',
                'text-h3',
                'text-h4',
                'text-body-large',
                'text-body',
                'text-body-small',
                'text-caption',
                'text-overline'
            ]
        }
    }
})

export function cn(...inputs: ClassValue[]): string {
    return customTwMerge(clsx(inputs))
}

export const breakpoints = {
    sm: 640,
    md: 768,
    lg: 1024,
    tablet: 768,
    desktop: 1050,
    xl: 1280,
    '2xl': 1536,
} as const

export const focusClasses = 'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2'

export const transitionClasses = 'transition-[color,background-color,border-color,box-shadow,opacity] duration-normal ease-out'

export function getSystemThemePreference(): 'light' | 'dark' {
    if (typeof window !== 'undefined') {
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    }
    return 'light'
}

export type InstructorNameSource = {
    firstName?: string | null
    lastName?: string | null
    email?: string | null
}

export function formatInstructorNames(
    instructors: InstructorNameSource[] | null | undefined,
    fallback: string
): string {
    if (!instructors || instructors.length === 0) return fallback
    const names = instructors
        .map((instructor) => [instructor.firstName, instructor.lastName].filter(Boolean).join(' ').trim())
        .map((name) => normalizeDisplayText(name))
        .filter((name): name is string => name !== null)
    if (names.length > 0) return names.join(', ')
    const emails = instructors
        .map((instructor) => normalizeDisplayText(instructor.email))
        .filter((email): email is string => email !== null)
    return emails.length > 0 ? emails.join(', ') : fallback
}

const DISPLAY_TEXT_PLACEHOLDERS = new Set(['', '-', '—'])

export function normalizeDisplayText(value?: string | null): string | null {
    if (typeof value !== 'string') {
        return null
    }

    const normalized = value.trim()
    return DISPLAY_TEXT_PLACEHOLDERS.has(normalized) ? null : normalized
}

export function splitInstructorNames(value?: string | null): string[] {
    if (typeof value !== 'string') {
        return []
    }
    return value
        .split(',')
        .map((item) => normalizeDisplayText(item))
        .filter((item): item is string => item !== null)
}

export function resolveInstructorCount(names: string[], declaredCount?: number | null): number {
    const normalizedDeclaredCount = typeof declaredCount === 'number' && declaredCount > 0 ? declaredCount : 0
    return Math.max(names.length, normalizedDeclaredCount)
}
